import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import {
  folioSectionLabels,
  type FolioExtraction,
  type FolioHoursCells,
  type FolioLeg,
} from '@@api/flightFolioScan.shared'

// Read-only tables mirroring the paper Flight Folio Technical Log sheet.
// Text only — every value is rendered exactly as the extraction transcribed it.

function display(value: string | null | undefined): string {
  return value?.trim() ? value : '—'
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold text-content-primary-bold leading-5">{children}</p>
  )
}

// 2-column label/value table for the simple sections
function KeyValueTable({ rows }: { rows: Array<[string, string | null | undefined]> }) {
  return (
    <Table>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell className="text-content-primary-subtle w-1/2 whitespace-normal">
              {label}
            </TableCell>
            <TableCell className="text-content-primary-bold">{display(value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// Rows shared by B/Fwd, each leg, and Totals
const hoursRows: Array<[string, keyof FolioHoursCells]> = [
  ['Flight time', 'flightTime'],
  ['Airframe hours', 'airframeHours'],
  ['Engine hours 1', 'engineHoursNo1'],
  ['Engine hours 2', 'engineHoursNo2'],
  ['Engine hours 3', 'engineHoursNo3'],
  ['Engine cycles 1', 'engineCyclesNo1'],
  ['Engine cycles 2', 'engineCyclesNo2'],
  ['Engine cycles 3', 'engineCyclesNo3'],
  ['Total landings', 'totalLandings'],
  ['APU hours', 'apuHours'],
]

// Rows that exist only on leg columns
const legOnlyRows: Array<[string, keyof FolioLeg]> = [
  ['Date', 'date'],
  ['From', 'from'],
  ['To', 'to'],
  ['PAX', 'pax'],
  ['CPT', 'crewCpt'],
  ['F/O', 'crewFo'],
  ['Other crew', 'crewOther'],
  ['Off block UTC', 'offBlockUtc'],
  ['T/O UTC', 'takeOffUtc'],
  ['LND UTC', 'landingUtc'],
  ['On block UTC', 'onBlockUtc'],
  ['Dist NM', 'distNm'],
  ['Dist KM', 'distKm'],
]

// The main grid, transposed for mobile: field labels down the first column,
// then B/Fwd | Leg 1..n | Totals across. Scrolls horizontally.
function LegsTable({
  broughtForward,
  legs,
  totals,
}: {
  broughtForward: FolioHoursCells
  legs: FolioLeg[]
  totals: FolioHoursCells
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead>B/Fwd</TableHead>
          {legs.map((_, i) => (
            <TableHead key={i}>Leg {i + 1}</TableHead>
          ))}
          <TableHead>Totals</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {legOnlyRows.map(([label, key]) => (
          <TableRow key={key}>
            <TableCell className="text-content-primary-subtle">{label}</TableCell>
            <TableCell className="text-content-primary-bold">—</TableCell>
            {legs.map((leg, i) => (
              <TableCell key={i} className="text-content-primary-bold">
                {display(leg[key])}
              </TableCell>
            ))}
            <TableCell className="text-content-primary-bold">—</TableCell>
          </TableRow>
        ))}
        {hoursRows.map(([label, key]) => (
          <TableRow key={key}>
            <TableCell className="text-content-primary-subtle">{label}</TableCell>
            <TableCell className="text-content-primary-bold">
              {display(broughtForward[key])}
            </TableCell>
            {legs.map((leg, i) => (
              <TableCell key={i} className="text-content-primary-bold">
                {display(leg[key])}
              </TableCell>
            ))}
            <TableCell className="text-content-primary-bold">{display(totals[key])}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const ectmRows: Array<[string, keyof FolioExtraction['ectm']]> = [
  ['Weight', 'weight'],
  ['OAT/SAT', 'oatSat'],
  ['IAS', 'ias'],
  ['TAS', 'tas'],
  ['Mach', 'mach'],
  ['FL', 'fl'],
  ['Fuel flow', 'fuelFlow'],
  ['N1/Prop', 'n1Prop'],
  ['N2/NG', 'n2Ng'],
  ['ITT', 'itt'],
  ['Oil press', 'oilPress'],
  ['Oil temp', 'oilTemp'],
  ['Amps', 'amps'],
  ['Fuel temp', 'fuelTemp'],
  ['Bleed press', 'bleedPress'],
]

function EctmTable({ ectm }: { ectm: FolioExtraction['ectm'] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead>1</TableHead>
          <TableHead>2</TableHead>
          <TableHead>3</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ectmRows.map(([label, key]) => (
          <TableRow key={key}>
            <TableCell className="text-content-primary-subtle">{label}</TableCell>
            <TableCell className="text-content-primary-bold">{display(ectm[key].value1)}</TableCell>
            <TableCell className="text-content-primary-bold">{display(ectm[key].value2)}</TableCell>
            <TableCell className="text-content-primary-bold">{display(ectm[key].value3)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function FlightFolioScanResult({ extracted }: { extracted: FolioExtraction }) {
  const { header, totalsBroughtForward, legs, totals, fuel, deice, oil, ectm } = extracted
  const { inspections, signOff, defects, warnings } = extracted

  return (
    <div className="flex flex-col gap-5">
      {warnings.length > 0 && (
        <div className="rounded-xl bg-surface-L2 px-4 py-3">
          <p className="text-sm font-semibold text-content-primary-bold leading-5">
            Check these fields
          </p>
          {warnings.map((warning, i) => (
            <p key={i} className="text-sm text-content-primary-subtle leading-5">
              {warning}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.header}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Aircraft type', header.aircraftType],
            ['Registration no.', header.registrationNo],
            ['Flight no.', header.flightNo],
            ['Nature of flight', header.natureOfFlight],
            ['Log no.', header.logNo],
          ]}
        />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.legs}</SectionTitle>
        <LegsTable broughtForward={totalsBroughtForward} legs={legs} totals={totals} />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.fuel}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Planned uplift LTS', fuel.plannedUpliftLts],
            ['Actual uplift LTS', fuel.actualUpliftLts],
            ['Fuel doc no.', fuel.fuelDocNo],
            ['Depart total LBS', fuel.departTotalLbs],
            ['Arrival total LBS', fuel.arrivalTotalLbs],
            ['LBS used', fuel.lbsUsed],
          ]}
        />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.deice}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Start', deice.start],
            ['Stop', deice.stop],
            ['Type', deice.type],
            ['Ratio (mix)', deice.ratioMix],
          ]}
        />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.oil}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Engine oil uplift 1', oil.engineOilUpliftNo1],
            ['Engine oil uplift 2', oil.engineOilUpliftNo2],
            ['Engine oil uplift 3', oil.engineOilUpliftNo3],
            ['APU oil uplift', oil.apuOilUplift],
            ['HYD oil uplift', oil.hydOilUplift],
            ['PSI dif', oil.psiDif],
          ]}
        />
      </div>

      {ectmRows.some(([, key]) =>
        [ectm[key].value1, ectm[key].value2, ectm[key].value3].some((v) => v.trim())
      ) && (
        <>
          <hr className="border-border-L1" />
          <div className="flex flex-col gap-2">
            <SectionTitle>{folioSectionLabels.ectm}</SectionTitle>
            <EctmTable ectm={ectm} />
          </div>
        </>
      )}

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.inspections}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Next scheduled', inspections.nextScheduled],
            ['Due hourly', inspections.dueHourly],
            ['Due calendar', inspections.dueCalendar],
          ]}
        />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.signOff}</SectionTitle>
        <KeyValueTable
          rows={[
            ['Pre-flight captain', signOff.preFlightCaptainName],
            ['Pre-flight licence no.', signOff.preFlightLicenceNumber],
            ['Pre-flight date', signOff.preFlightDate],
            ['Post-flight captain', signOff.postFlightCaptainName],
            ['Post-flight licence no.', signOff.postFlightLicenceNumber],
            ['Post-flight date', signOff.postFlightDate],
          ]}
        />
      </div>

      <hr className="border-border-L1" />

      <div className="flex flex-col gap-2">
        <SectionTitle>{folioSectionLabels.defects}</SectionTitle>
        {defects.length === 0 ? (
          <p className="text-sm text-content-primary-subtle leading-5">No defects recorded</p>
        ) : (
          defects.map((defect, i) => (
            <div key={i} className="flex flex-col gap-2">
              {i > 0 && <hr className="border-border-L1" />}
              <p className="text-sm text-content-primary-subtle leading-5">Defect {i + 1}</p>
              <KeyValueTable
                rows={[
                  ['Defect Y/N', defect.defectYn],
                  ['Description', defect.description],
                  ['A/D', defect.actionCode],
                  ['Signature', defect.signature],
                  ['Date', defect.date],
                  ['Action taken', defect.actionTaken],
                  ['MEL ref', defect.melRef],
                  ['Stamp', defect.stamp],
                ]}
              />
            </div>
          ))
        )}
      </div>
    </div>
  )
}
